import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PRODUCT_IDS } from "../productContext";
import {
  canActivateRoute,
  resolveDashboardMenuPath,
  resolveDashboardRelativePath,
  resolveRoutePath,
} from "../routeGuards";
import { traceNav, traceSprint1BNavRefactor } from "../routeTelemetry";
import { useProductContext } from "../providers/ProductContextProvider";

function buildNavCtx({ locale, product, session, entitlements, isLegacyAlias, useLegacyAlias }) {
  return {
    locale,
    product,
    session,
    entitlements,
    useLegacyAlias: useLegacyAlias ?? isLegacyAlias ?? true,
  };
}

/**
 * Locale-aware dashboard navigation via stable Route IDs.
 */
export function useDashboardNavigate() {
  const navigate = useNavigate();
  const { locale, product, session, entitlements, isLegacyAlias } = useProductContext();

  const resolve = useCallback(
    (routeId, options = {}) => {
      const ctx = buildNavCtx({
        locale: options.locale ?? locale,
        product: options.product ?? product,
        session,
        entitlements,
        isLegacyAlias,
        useLegacyAlias: options.useLegacyAlias,
      });
      const resolved = resolveRoutePath(routeId, {
        ...ctx,
        params: options.params,
      });
      traceSprint1BNavRefactor({ action: "resolve", routeId, path: resolved?.path, ctx });
      return resolved;
    },
    [locale, product, session, entitlements, isLegacyAlias]
  );

  const pathFor = useCallback(
    (routeId, options = {}) => {
      const resolved = resolve(routeId, options);
      return resolved?.path ?? null;
    },
    [resolve]
  );

  const pathForMenu = useCallback(
    (menuPath, options = {}) => {
      const ctx = buildNavCtx({
        locale,
        product: PRODUCT_IDS.AGENT,
        session,
        entitlements,
        isLegacyAlias,
        useLegacyAlias: options.useLegacyAlias,
      });
      const resolved = resolveDashboardMenuPath(menuPath, ctx);
      traceSprint1BNavRefactor({ action: "pathForMenu", menuPath, path: resolved?.path, ctx });
      return resolved?.path ?? null;
    },
    [locale, session, entitlements, isLegacyAlias]
  );

  const pathForRelative = useCallback(
    (relativePath, options = {}) => {
      const ctx = buildNavCtx({
        locale,
        product: PRODUCT_IDS.AGENT,
        session,
        entitlements,
        isLegacyAlias,
        useLegacyAlias: options.useLegacyAlias,
      });
      const resolved = resolveDashboardRelativePath(relativePath, ctx);
      traceSprint1BNavRefactor({
        action: "pathForRelative",
        relativePath,
        path: resolved?.path,
        ctx,
      });
      return resolved?.path ?? null;
    },
    [locale, session, entitlements, isLegacyAlias]
  );

  const to = useCallback(
    (routeId, options = {}) => {
      const ctx = buildNavCtx({
        locale: options.locale ?? locale,
        product: options.product ?? product,
        session,
        entitlements,
        isLegacyAlias,
        useLegacyAlias: options.useLegacyAlias,
      });

      const guard = canActivateRoute(routeId, ctx);
      if (!guard.allowed) {
        traceNav({
          from: typeof window !== "undefined" ? window.location.pathname : "",
          to: routeId,
          resolvedBy: "blocked",
          reason: guard.reason,
        });
        traceSprint1BNavRefactor({ action: "to_blocked", routeId, reason: guard.reason });
        return null;
      }

      const resolved = resolveRoutePath(routeId, {
        ...ctx,
        params: options.params,
      });
      if (!resolved?.path) {
        traceNav({
          from: typeof window !== "undefined" ? window.location.pathname : "",
          to: routeId,
          resolvedBy: "failed",
        });
        return null;
      }

      let target = resolved.path;
      if (options.search) {
        target += options.search.startsWith("?") ? options.search : `?${options.search}`;
      }
      if (options.hash) {
        target += options.hash.startsWith("#") ? options.hash : `#${options.hash}`;
      }

      traceNav({
        from: typeof window !== "undefined" ? window.location.pathname : "",
        to: target,
        resolvedBy: "routeRegistry",
        routeId,
        legacyAliasUsed: resolved.legacyAliasUsed,
      });
      traceSprint1BNavRefactor({ action: "to", routeId, target, legacyAliasUsed: resolved.legacyAliasUsed });

      navigate(target, { replace: options.replace, state: options.state });
      return target;
    },
    [navigate, locale, product, session, entitlements, isLegacyAlias]
  );

  const toMenu = useCallback(
    (menuPath, options = {}) => {
      const target = pathForMenu(menuPath, options);
      if (!target) return null;
      traceSprint1BNavRefactor({ action: "toMenu", menuPath, target });
      navigate(target, { replace: options.replace, state: options.state });
      return target;
    },
    [navigate, pathForMenu]
  );

  return { to, toMenu, pathFor, pathForMenu, pathForRelative, resolve };
}

export default useDashboardNavigate;
